import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, RefreshControl, Alert, Share, Platform } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import type { CareNote } from '../types/care';
import { careNotesService } from '../services/careNotesService';

interface CareNotesScreenProps {
  recipientId: string;
  recipientName: string;
  currentUserName: string;
  onBack: () => void;
  canEdit: boolean;
}

const CareNotesScreen: React.FC<CareNotesScreenProps> = ({
  recipientId,
  recipientName,
  currentUserName,
  onBack,
  canEdit,
}) => {
  const [notes, setNotes] = useState<CareNote[]>([]);
  const [filtered, setFiltered] = useState<CareNote[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addBody, setAddBody] = useState('');
  const [addType, setAddType] = useState<'note' | 'incident'>('note');

  const load = useCallback(async () => {
    const list = await careNotesService.list(recipientId);
    setNotes(list);
    setFiltered(careNotesService.search(list, search));
  }, [recipientId, search]);

  useEffect(() => {
    load().finally(() => { setLoading(false); setRefreshing(false); });
  }, [load]);

  useEffect(() => {
    setFiltered(careNotesService.search(notes, search));
  }, [notes, search]);

  const handleAdd = async () => {
    const body = addBody.trim();
    if (!body) {
      Alert.alert('Required', 'Enter a note.');
      return;
    }
    await careNotesService.add(recipientId, {
      body,
      type: addType,
      createdBy: '', // optional; could pass current user id
      createdByName: currentUserName,
    });
    setAddBody('');
    setAddType('note');
    setShowAdd(false);
    load();
  };

  const handleExport = () => {
    const text = careNotesService.exportAsText(filtered.length ? filtered : notes, recipientName);
    if (Platform.OS === 'web') {
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `care-notes-${recipientName.replace(/\s/g, '-')}-${new Date().toISOString().slice(0, 10)}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      Share.share({ message: text, title: `Care notes – ${recipientName}` });
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack}>
          <Text style={styles.backText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.title}>Notes & incidents</Text>
        <View style={styles.headerRight}>
          <TouchableOpacity onPress={handleExport}>
            <Text style={styles.exportText}>Export</Text>
          </TouchableOpacity>
        </View>
      </View>
      <Text style={styles.subtitle}>{recipientName}</Text>
      <Text style={styles.hint}>Timestamped notes. Patterns emerge over time. Export for doctors/family.</Text>

      <AppTextInput
        style={styles.search}
        placeholder="Search notes..."
        value={search}
        onChangeText={setSearch}
      />

      {canEdit && (
        <TouchableOpacity style={styles.addBtn} onPress={() => setShowAdd(true)}>
          <Text style={styles.addBtnText}>+ Add note or incident</Text>
        </TouchableOpacity>
      )}

      <ScrollView
        style={styles.list}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} />}
      >
        {loading ? (
          <Text style={styles.loading}>Loading...</Text>
        ) : filtered.length === 0 ? (
          <Text style={styles.empty}>No notes yet. {canEdit ? 'Tap "+ Add note" to log something.' : ''}</Text>
        ) : (
          filtered.map((n) => (
            <View key={n.id} style={[styles.noteCard, n.type === 'incident' && styles.noteCardIncident]}>
              <View style={styles.noteHeader}>
                <Text style={styles.noteType}>{n.type === 'incident' ? '⚠ Incident' : 'Note'}</Text>
                <Text style={styles.noteDate}>{new Date(n.createdAt).toLocaleString()}</Text>
              </View>
              <Text style={styles.noteBody}>{n.body}</Text>
              {n.createdByName && <Text style={styles.noteBy}>— {n.createdByName}</Text>}
            </View>
          ))
        )}
      </ScrollView>

      {showAdd && (
        <View style={styles.addOverlay}>
          <View style={styles.addCard}>
            <Text style={styles.addTitle}>Add note or incident</Text>
            <AppTextInput
              style={styles.addInput}
              placeholder="e.g. Seemed confused today / Skipped lunch / Fell but no injury"
              value={addBody}
              onChangeText={setAddBody}
              multiline
              numberOfLines={3}
            />
            <View style={styles.typeRow}>
                <TouchableOpacity
                  style={[styles.typeBtn, addType === 'note' && styles.typeBtnOn]}
                  onPress={() => setAddType('note')}
                >
                  <Text style={[styles.typeBtnText, addType === 'note' && styles.typeBtnTextOn]}>Note</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.typeBtn, addType === 'incident' && styles.typeBtnOn]}
                  onPress={() => setAddType('incident')}
                >
                  <Text style={[styles.typeBtnText, addType === 'incident' && styles.typeBtnTextOn]}>Incident</Text>
                </TouchableOpacity>
            </View>
            <View style={styles.addActions}>
              <TouchableOpacity onPress={() => { setShowAdd(false); setAddBody(''); }}>
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleAdd}>
                <Text style={styles.saveBtnText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F2F2F7' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingTop: 56, paddingBottom: 8, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E5E5E7' },
  backText: { fontSize: 16, color: '#007AFF' },
  title: { fontSize: 18, fontWeight: '700', color: '#1C1C1E' },
  headerRight: {},
  exportText: { fontSize: 15, color: '#007AFF', fontWeight: '500' },
  subtitle: { fontSize: 14, color: '#8E8E93', paddingHorizontal: 20, paddingTop: 8 },
  hint: { fontSize: 12, color: '#8E8E93', fontStyle: 'italic', paddingHorizontal: 20, paddingBottom: 12 },
  search: { marginHorizontal: 20, marginBottom: 12, borderWidth: 1, borderColor: '#E5E5E7', borderRadius: 10, padding: 12, fontSize: 16, backgroundColor: '#fff' },
  addBtn: { marginHorizontal: 20, marginBottom: 16, padding: 14, borderRadius: 10, backgroundColor: '#007AFF', alignItems: 'center' },
  addBtnText: { fontSize: 15, fontWeight: '600', color: '#fff' },
  list: { flex: 1, paddingHorizontal: 20 },
  loading: { padding: 20 },
  empty: { padding: 20, color: '#8E8E93', fontStyle: 'italic' },
  noteCard: { backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 12 },
  noteCardIncident: { borderLeftWidth: 4, borderLeftColor: '#FF9500' },
  noteHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 },
  noteType: { fontSize: 12, fontWeight: '600', color: '#8E8E93' },
  noteDate: { fontSize: 12, color: '#8E8E93' },
  noteBody: { fontSize: 15, color: '#1C1C1E', lineHeight: 22 },
  noteBy: { fontSize: 12, color: '#8E8E93', marginTop: 6 },
  addOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', padding: 20 },
  addCard: { backgroundColor: '#fff', borderRadius: 16, padding: 20 },
  addTitle: { fontSize: 18, fontWeight: '700', marginBottom: 12 },
  addInput: { borderWidth: 1, borderColor: '#E5E5E7', borderRadius: 10, padding: 12, fontSize: 16, minHeight: 80, textAlignVertical: 'top', marginBottom: 12 },
  typeRow: { flexDirection: 'row', gap: 12, marginBottom: 16 },
  typeBtn: { flex: 1, padding: 12, borderRadius: 8, backgroundColor: '#F2F2F7', alignItems: 'center' },
  typeBtnOn: { backgroundColor: '#007AFF' },
  typeBtnText: { fontSize: 14, fontWeight: '600', color: '#1C1C1E' },
  typeBtnTextOn: { color: '#fff' },
  addActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12 },
  cancelText: { fontSize: 15, color: '#8E8E93' },
  saveBtn: { backgroundColor: '#007AFF', paddingHorizontal: 20, paddingVertical: 12, borderRadius: 8 },
  saveBtnText: { fontSize: 15, fontWeight: '600', color: '#fff' },
});

export default CareNotesScreen;
